import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { take } from 'rxjs';
import { DateTime } from 'luxon';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';

import { KSeFApiService, SetKsefCertificatePayload } from 'shared/services/api/ksef.api.service';
import { APIResponse, UserSettings } from 'fakturnia-shared';
import { SnackbarService } from 'shared/services/snackbar.service';
import { DialogService } from 'shared/services/dialog.service';
import { SettingsService } from 'shared/services/settings.service';
import { settings } from 'environments/environment';
import { KsefConnectionResultDialogComponent } from 'shared/dialogs/ksef-connection-result-dialog/ksef-connection-result-dialog.component';
import { KsefTokenDialogComponent, KsefTokenDialogResult } from 'shared/dialogs/ksef-token-dialog/ksef-token-dialog.component';
import { KsefCertificateDialogComponent, KsefCertificateDialogResult } from 'shared/dialogs/ksef-certificate-dialog/ksef-certificate-dialog.component';
import { KsefSetupAlertComponent } from 'shared/components/ksef-setup-alert/ksef-setup-alert.component';
import { KsefSyncDialogComponent, KsefSyncDialogResult } from 'shared/dialogs/ksef-sync-dialog/ksef-sync-dialog.component';
import { DateFormatterComponent } from 'shared/components/date-formatter/date-formatter.component';
import { SynchronizationService } from 'shared/services/synchronization.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-ksef',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    KsefSetupAlertComponent,
    MatIconModule,
    MatTooltipModule,
    MatDialogModule,
    DateFormatterComponent
  ],
  templateUrl: './ksef.component.html',
  styleUrl: './ksef.component.scss'
})
export class KsefComponent implements OnInit, OnDestroy {

  @ViewChild(KsefSetupAlertComponent) private _setupAlert?: KsefSetupAlertComponent;

  public ksefConnection: any = null;
  public isLoading: boolean = true;
  public isCheckingConnection: boolean = false;
  public enableSubscriptions = settings.enableSubscriptions;
  public isSubscriptionExpired = false;
  private _subscriptions: Subscription[] = [];

  public get isCertificateConnection(): boolean {
    return this.ksefConnection?.connectionType === 'CERTIFICATE';
  }

  public get isTokenConnection(): boolean {
    return !this.isCertificateConnection;
  }

  public get hasOwnerPermission(): boolean {
    return this.ksefConnection?.permissions?.includes('Owner') || false;
  }

  public get hasInvoiceReadPermission(): boolean {
    if (this.hasOwnerPermission) return true;
    return this.ksefConnection?.permissions?.includes('InvoiceRead') || false;
  }

  public get hasInvoiceWritePermission(): boolean {
    if (this.hasOwnerPermission) return true;
    return this.ksefConnection?.permissions?.includes('InvoiceWrite') || false;
  }

  public get hasPermissionsData(): boolean {
    return Array.isArray(this.ksefConnection?.permissions);
  }

  public get hasMissingPermissions(): boolean {
    if (!this.hasPermissionsData) return false;
    return !this.hasInvoiceReadPermission || !this.hasInvoiceWritePermission;
  }

  public get certificateExpiresAt(): DateTime | null {
    if (!this.ksefConnection?.certificateExpiresAt) return null;
    return DateTime.fromISO(this.ksefConnection.certificateExpiresAt);
  }

  public get isCertificateExpired(): boolean {
    const expiresAt = this.certificateExpiresAt;
    if (!expiresAt) return false;
    return expiresAt < DateTime.now();
  }

  public get isCertificateExpiringSoon(): boolean {
    const expiresAt = this.certificateExpiresAt;
    if (!expiresAt) return false;
    const now = DateTime.now();
    return expiresAt > now && expiresAt < now.plus({ months: 1 });
  }

  // Mapa tłumaczeń uprawnień KSeF
  public permissionTranslations: Record<string, string> = {
    'InvoiceWrite': 'Tworzenie faktur',
    'InvoiceRead': 'Odczyt faktur',
    'CredentialsRead': 'Odczyt uprawnień',
    'CredentialsManage': 'Zarządzanie uprawnieniami',
    'PaymentConfirmationWrite': 'Potwierdzanie płatności',
    'SelfInvoicing': 'Samofakturowanie',
    'TaxRepresentative': 'Przedstawiciel podatkowy',
    'Enforcement': 'Egzekucja',
    'SubjectRead': 'Odczyt podmiotów',
    'SubjectWrite': 'Tworzenie podmiotów',
    'Owner': 'Właściciel'
  };

  constructor(
    private _ksefApiService: KSeFApiService,
    private _snackbarService: SnackbarService,
    private _dialog: MatDialog,
    private _dialogService: DialogService,
    private _synchronizationService: SynchronizationService,
    private _settingsService: SettingsService
  ) { }

  ngOnInit(): void {
    this._getConnection();
    if (this.enableSubscriptions) {
      this._subscriptions.push(
        this._settingsService.currentUserSettings.subscribe((us: UserSettings) => {
          if (us?.subscription?.validUntil) {
            this.isSubscriptionExpired = DateTime.fromISO(us.subscription.validUntil) < DateTime.now();
          }
        })
      );
    }
  }

  public translatePermission(permission: string): string {
    return this.permissionTranslations[permission] || permission;
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  /**
   * Tłumaczy techniczne błędy KSeF na zrozumiałe komunikaty dla użytkownika.
   * Aby dodać nowy wyjątek, wystarczy dodać wpis do tablicy poniżej.
   */
  private static readonly KSEF_ERROR_TRANSLATIONS: { pattern: string; message: string }[] = [
    { pattern: 'bad decrypt', message: 'Nieprawidłowe hasło klucza prywatnego' },
  ];

  public translateKsefError(error: string): string {
    if (!error) return error;
    const match = KsefComponent.KSEF_ERROR_TRANSLATIONS.find(t => error.includes(t.pattern));
    return match ? match.message : error;
  }

  public openTokenDialog(): void {
    const dialogRef = this._dialog.open(KsefTokenDialogComponent, {
      data: {
        hasToken: this.ksefConnection?.hasToken,
        nip: this.ksefConnection?.nip
      },
      maxWidth: '1100px',
      width: '95vw',
      maxHeight: '90vh'
    });

    dialogRef.afterClosed().subscribe((result: KsefTokenDialogResult | undefined) => {
      if (result?.token) {
        this._saveToken(result.token);
      }
    });
  }

  public openCertificateDialog(): void {
    const dialogRef = this._dialog.open(KsefCertificateDialogComponent, {
      data: {
        hasCertificate: this.ksefConnection?.hasCertificate,
        nip: this.ksefConnection?.nip
      },
      maxWidth: '1100px',
      width: '95vw',
      maxHeight: '90vh'
    });

    dialogRef.afterClosed().subscribe((result: KsefCertificateDialogResult | undefined) => {
      if (result) {
        this._saveCertificate(result);
      }
    });
  }

  public openSyncDialog(): void {
    const dialogRef = this._dialog.open(KsefSyncDialogComponent, {
      data: {
        isAuthorized: this.ksefConnection?.isAuthorized
      },
      panelClass: 'fakturnia-responsive-dialog',
      maxWidth: '500px',
      width: '95vw'
    });

    dialogRef.afterClosed().subscribe((result: KsefSyncDialogResult | undefined) => {
      if (result?.synced) {
        // Odśwież dane połączenia (lastSyncAt itd.)
        this._getConnection();
        // Synchronizuj Dexie z backendem
        this._synchronizationService.synchronize('ksef-manual-sync');
      }
    });
  }

  private _saveToken(token: string): void {
    this._ksefApiService.setToken(token)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          const syncData = response?.data?.sync;
          const syncError = response?.data?.syncError;

          if (syncData) {
            const total = (syncData.inserted || 0) + (syncData.updated || 0);
            this._snackbarService.showSnackBar(
              `Token KSeF zapisany. Zsynchronizowano ${total} faktur.`,
              'success', 5000
            );
            // Synchronizuj Dexie z backendem (nowe faktury)
            this._synchronizationService.synchronize('ksef-token-sync');
          } else if (syncError) {
            this._snackbarService.showSnackBar(
              `Token zapisany, ale synchronizacja nie powiodła się: ${syncError}`,
              'warning', 5000
            );
          } else {
            this._snackbarService.showSnackBar('Token KSeF został zapisany.', 'success', 3000);
          }

          // Po zapisaniu tokena: zweryfikuj połączenie i odśwież widok
          this._ksefApiService.initSession()
            .pipe(take(1))
            .subscribe({
              next: () => { this._getConnection(true); this._setupAlert?.refreshStatus(); },
              error: () => { this._getConnection(true); this._setupAlert?.refreshStatus(); }
            });
        },
        error: (error) => {
          console.error('Error setting KSeF token:', error);
          this._snackbarService.showSnackBar('Nie udało się zapisać tokena.', 'error', 5000);
        }
      });
  }

  private _saveCertificate(payload: SetKsefCertificatePayload): void {
    this._ksefApiService.setCertificate(payload)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this._snackbarService.showSnackBar('Certyfikat KSeF został zapisany.', 'success', 3000);

          this._ksefApiService.initSession()
            .pipe(take(1))
            .subscribe({
              next: () => { this._getConnection(true); this._setupAlert?.refreshStatus(); },
              error: () => { this._getConnection(true); this._setupAlert?.refreshStatus(); }
            });
        },
        error: (error) => {
          console.error('Error setting KSeF certificate:', error);
          this._snackbarService.showSnackBar('Nie udało się zapisać certyfikatu KSeF.', 'error', 5000);
        }
      });
  }

  public checkConnectionStatus(): void {
    this.isCheckingConnection = true;
    
    // This forces a new session initialization on the backend
    this._ksefApiService.initSession()
      .pipe(take(1))
      .subscribe({
        next: (response: APIResponse) => {
          if(response.success === false) {
            console.error('Error initializing KSeF session:', response.message);
            this.isCheckingConnection = false;
            return;
          }

          this._getConnection(true);
        },
        error: () => {
          this.isCheckingConnection = false;
        }
      });
  }

  public deactivateConnection(): void {
    this._dialogService.confirmDialog({
      title: 'Dezaktywacja połączenia z KSeF',
      message: 'Czy na pewno chcesz dezaktywować połączenie z KSeF? Synchronizacja faktur zostanie wstrzymana.',
      confirmText: 'Dezaktywuj',
      cancelText: 'Anuluj',
      type: 'warning',
      showIcon: true
    }).subscribe(confirmed => {
      if (!confirmed) return;

      this._ksefApiService.deactivateConnection()
        .pipe(take(1))
        .subscribe({
          next: (response: APIResponse) => {
            if (response.success) {
              this._snackbarService.showSnackBar('Połączenie z KSeF zostało dezaktywowane.', 'success', 3000);
              this._getConnection();
            } else {
              this._snackbarService.showSnackBar(response.message || 'Nie udało się dezaktywować połączenia.', 'error', 5000);
            }
          },
          error: (error) => {
            console.error('Error deactivating KSeF connection:', error);
            this._snackbarService.showSnackBar('Wystąpił błąd podczas dezaktywacji połączenia.', 'error', 5000);
          }
        });
    });
  }

  private _getConnection(triggerSnackbar = false): void {
    this._ksefApiService.get()
      .pipe(take(1))
      .subscribe({
        next: (response: APIResponse) => {
          this.isLoading = false;
          this.isCheckingConnection = false;

          if(response.success === false) {
            this._snackbarService.showSnackBar('Nie udało się pobrać statusu połączenia z KSeF', 'error', 5000);
            return;
          }

          this.ksefConnection = response.data;

          if(!triggerSnackbar) return;
          
          // Otwórz dialog z wynikiem
          this._dialog.open(KsefConnectionResultDialogComponent, {
            data: {
              success: response.success,
              isAuthorized: this.ksefConnection.isAuthorized,
              permissions: this.ksefConnection.permissions,
              connectionType: this.ksefConnection.connectionType,
              nip: this.ksefConnection.nip,
              lastCheckAt: this.ksefConnection.lastCheckAt,
              lastError: this.ksefConnection.lastError,
              lastErrorAt: this.ksefConnection.lastErrorAt
            },
            panelClass: 'ksef-connection-result-dialog',
            maxWidth: '450px',
            width: '100%'
          });
        },
        error: () => {
          this.isLoading = false;
          this.isCheckingConnection = false;
        }
      });
  }
}